import { Card } from '@acceso/design-system';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { GeneralHeader } from '@/components/HeaderPage/HeaderPage';
import {
  FrameAction,
  FrameActionType,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { selectPresenceCollection } from '@/features/broadcast-schedule-program/helpers/select-presence-collection';
import { usePresenceCollectionsSync } from '@/features/broadcast-schedule-program/hooks/usePresenceCollectionsSync';
import { useSegmentFeaturedPeoplePermissions } from '@/features/broadcast-schedule-program/hooks/useSegmentFeaturedPeoplePermissions';
import { useDeleteSegmentPeople } from '@/features/segments/api/delete-segment-people';
import { usePatchSegmentPeople } from '@/features/segments/api/patch-segment-people';
import { TPostSegmentPeople, TSegmentPeople } from '@/features/segments/types';
import { usePresencePreviewBox } from '@/hooks/use-presence-preview-box';

import { ESegmentToolkit } from '../../types';
import { BroadcastScheduleProgramBodySegmentToolkitNavigation } from '../BroadcastScheduleProgramBodySegmentToolkitNavigation';

import { BroadcastScheduleProgramBodySegmentToolkitPeopleForm } from './components/BroadcastScheduleProgramBodySegmentToolkitPeopleForm';
import { BroadcastScheduleProgramBodySegmentToolkitPeopleTable } from './components/BroadcastScheduleProgramBodySegmentToolkitPeopleTable';

function BroadcastScheduleProgramBodySegmentToolkitPeopleInner() {
  const {
    formBox,
    personaData,
    activeSegment,
    isGlobalView,
    globalViewSegments,
    globalViewOnPreview,
    setVisibleBox,
    setFormBox,
    setPersonaData,
    setActiveSegment,
    handleTooltikClick,
    setSegmentItems,
    videoControlRef,
  } = useBroadcastScheduleProgram();
  const { mutateAsync: deleteLogoSegment } = useDeleteSegmentPeople();
  const { mutateAsync: patchPeople } = usePatchSegmentPeople();
  const { apply: applyCollectionsSync } = usePresenceCollectionsSync();
  const { t } = useTranslation();

  const peopleItems = useMemo(
    () =>
      selectPresenceCollection(
        { isGlobalView, activeSegment, globalViewSegments },
        'segment_peoples',
        'people'
      ),
    [isGlobalView, activeSegment, globalViewSegments]
  );

  const personaDataIdRef = useRef(personaData?.id);
  personaDataIdRef.current = personaData?.id;

  const previewPresenceBox = usePresencePreviewBox({
    referenceStartDatetime: activeSegment?.start_datetime,
    setVisibleBox,
    videoControlRef,
  });

  const handlePreviewBox = useCallback(
    (box: TSegmentPeople, frame: FrameActionType = FrameAction.START, currentFrame?: number) => {
      if (personaDataIdRef.current !== box.id) {
        setPersonaData(box);
      }

      previewPresenceBox(box, frame, currentFrame);
    },
    [previewPresenceBox, setPersonaData]
  );

  const {
    canCreate: canCreatePerson,
    canUpdate: canUpdatePerson,
    canDelete: canDeletePerson,
  } = useSegmentFeaturedPeoplePermissions();

  const handleEdit = useCallback(
    (item: TSegmentPeople) => {
      if (!canUpdatePerson) return;
      setFormBox('people');
      setPersonaData(item);
    },
    [setFormBox, canUpdatePerson]
  );

  const handleCreatePerson = useCallback(() => {
    if (!canCreatePerson) return;
    setFormBox('people');
    setPersonaData(null);
  }, [setFormBox, setPersonaData, canCreatePerson]);

  const handleDeletePeople = useCallback(
    (item: TSegmentPeople) => {
      if (!canDeletePerson) return;
      deleteLogoSegment(item.id || '').then(() => {
        setActiveSegment(prev => {
          if (prev) {
            return {
              ...prev,
              segment_peoples: prev.segment_peoples?.filter(logo => logo.id !== item.id),
            };
          }
          return prev;
        });
        setSegmentItems(prev => {
          if (!prev) return prev;
          return prev.map(segment => {
            if (segment.id === activeSegment?.id) {
              return {
                ...segment,
                segment_peoples: segment.segment_peoples?.filter(people => people.id !== item.id),
              };
            }
            return segment;
          });
        });
      });
    },
    [canDeletePerson]
  );

  const handleEditPeopleInline = useCallback(
    (item: TPostSegmentPeople, silently?: boolean) => {
      if (!canUpdatePerson) return;
      const {
        id,
        segment_people_id,
        people_id,
        entity_score,
        start_datetime,
        end_datetime,
        parameters,
        review_state,
      } = item;
      const presenceId = segment_people_id || id;

      const peopleData = {
        id: presenceId,
        silently,
        body: {
          entity_score,
          start_datetime,
          end_datetime,
          parameters,
          people_id,
          review_state,
        },
      };

      patchPeople(peopleData).then(updatedPeople => {
        setActiveSegment(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            segment_peoples: prev.segment_peoples?.map(p =>
              p.id === updatedPeople.id ? updatedPeople : p
            ),
          };
        });
        setSegmentItems(prev => {
          if (!prev) return prev;
          return prev.map(segment => {
            if (segment.id === activeSegment?.id) {
              return {
                ...segment,
                segment_peoples: segment.segment_peoples?.map(p =>
                  p.id === updatedPeople.id ? updatedPeople : p
                ),
              };
            }
            return segment;
          });
        });
      });
    },
    [canUpdatePerson, patchPeople, setActiveSegment, setSegmentItems, activeSegment]
  );

  const handleEditPeopleInlineGlobal = useCallback(
    (item: TPostSegmentPeople, silently?: boolean) => {
      if (!canUpdatePerson) return;
      const presenceId = item.segment_people_id || item.id;
      patchPeople({
        id: presenceId,
        silently,
        body: {
          entity_score: item.entity_score,
          start_datetime: item.start_datetime,
          end_datetime: item.end_datetime,
          parameters: item.parameters,
          people_id: item.people_id,
          review_state: item.review_state,
        },
      }).then(() => {
        applyCollectionsSync(item.segment_id, 'segment_peoples', peoples =>
          peoples.map(p =>
            p.id !== item.id
              ? p
              : {
                  ...p,
                  people_id: item.people_id,
                  entity_score: item.entity_score,
                  start_datetime: item.start_datetime?.toString() ?? p.start_datetime,
                  end_datetime: item.end_datetime?.toString() ?? p.end_datetime,
                  parameters: {
                    ...p.parameters,
                    ...item.parameters,
                    bbox: item.parameters?.bbox ?? p.parameters.bbox,
                  },
                  review_state: item.review_state ?? p.review_state,
                }
          )
        );
      });
    },
    [canUpdatePerson, patchPeople, applyCollectionsSync]
  );

  const handleDeletePeopleGlobal = useCallback(
    (item: TSegmentPeople) => {
      if (!canDeletePerson) return;
      deleteLogoSegment(item.id || '').then(() => {
        applyCollectionsSync(item.segment_id, 'segment_peoples', peoples =>
          peoples.filter(p => p.id !== item.id)
        );
      });
    },
    [canDeletePerson, deleteLogoSegment, applyCollectionsSync]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLocaleLowerCase() === 'i') {
        event.preventDefault();
        handleCreatePerson();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCreatePerson]);

  const tableHandlers = isGlobalView
    ? {
        onPreview: globalViewOnPreview
          ? (box: TSegmentPeople, frame?: FrameActionType) => globalViewOnPreview(box, frame)
          : undefined,
        onUpdate: undefined,
        onUpdateInline: canUpdatePerson ? handleEditPeopleInlineGlobal : undefined,
        onDelete: canDeletePerson ? handleDeletePeopleGlobal : undefined,
      }
    : {
        onPreview: handlePreviewBox,
        onUpdate: canUpdatePerson ? handleEdit : undefined,
        onUpdateInline: canUpdatePerson ? handleEditPeopleInline : undefined,
        onDelete: canDeletePerson ? handleDeletePeople : undefined,
      };

  return (
    <Card
      classes={{
        rootSection: clsx('w-full flex-auto order-3 lg:order-2'),
        container: 'py-size-4',
      }}
    >
      <div className="flex w-full flex-col gap-2">
        {formBox !== 'people' || isGlobalView ? (
          <>
            <GeneralHeader
              name={t('Persona de interés')}
              buttonName={!isGlobalView && canCreatePerson ? t('Añadir') : undefined}
              onClick={!isGlobalView ? handleCreatePerson : undefined}
              onClose={() => handleTooltikClick(ESegmentToolkit.PERSONAS)}
            />
            <div className="flex max-h-[calc(100vh-236px)] min-h-[400px] flex-col overflow-y-auto">
              <BroadcastScheduleProgramBodySegmentToolkitPeopleTable
                items={peopleItems}
                {...tableHandlers}
              />
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <BroadcastScheduleProgramBodySegmentToolkitPeopleForm />
            <BroadcastScheduleProgramBodySegmentToolkitNavigation
              items={activeSegment?.segment_peoples || []}
              activeItem={personaData}
              onChangeData={setPersonaData}
            />
          </div>
        )}
      </div>
    </Card>
  );
}

export default function BroadcastScheduleProgramBodySegmentToolkitPeople() {
  return <BroadcastScheduleProgramBodySegmentToolkitPeopleInner />;
}
